import AvailableHelper from "../Availability/AvailableHelper";
import HoursHelper from "../Availability/HoursHelper";
import DateService from "../../../Common/Date/DateService";
import ItemViewHelper from "../Item/ItemViewHelper";
import ItemHelper from "../Item/ItemHelper";
import {ConfigApi, OrderType, ParsedCategory} from "../../../Api/types";
import {TFunction} from "i18next";
import {getParsedSessionOrderType} from "../../../../utils/order";
import {DateHelper, getHourTimeString} from "../../../Common/Date/DateHelper";
import ItemAvailabilityHelper from "../Item/ItemAvailabilityHelper";

class CategoryHelper {
    static getAvailableCategoryItems(availabilities: any[], pickupAt: string | undefined, items: any[]) {
        return items.filter((item: any) => {
            let isDisabled = item.disabled;
            if (item.type === "ITEM_GROUP") {
                isDisabled = item.items.every((subItem: Record<string, any>) => subItem.disabled);
            }

            if (isDisabled) return false;

            const availabilityIds: number[] = item.type === "ITEM_GROUP" ?
                Array.from(new Set(item.items.map((subItem: Record<string, any>) => subItem.availability_id))).filter(Boolean)
                : [item.availability_id].filter(Boolean);

            if (availabilityIds.length === 0) return true;

            return availabilityIds.some(id => {
                const availability = AvailableHelper.getAvailability(availabilities, id);
                return HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(pickupAt)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(pickupAt))
            })
        });
    }

    static getMenusForCategory(menus: any[], category: any) {
        return menus.filter((menu: any) => menu.categories.map((menuCategory: any) => menuCategory.category_id).includes(category.id));
    }

    static getMenusForCategoryByIds(category: Record<string, any>, menus: Record<string, any>[]) {
        return category.menuIds.reduce((acc: Record<string, any>, id: number) => {
            const menu = menus.find((menu) => menu.id === id);

            if (menu) {
                acc.push(menu);
            }

            return acc;
        }, []);
    }

    static getCategoryPosition(category: any, menus: any[]) {
        const positions = menus.map((menu: any) => menu.categories.find((menuCategory: any) => menuCategory.category_id === category.id)?.position)
            .filter(position => position || position === 0);

        return positions.length > 0 ? Math.min(...positions) : 0;
    }

    static findCategoriesForMenu(categories: Record<string, any>[], menus: Record<string, any>[], menuId: number | undefined): {
        menu: Record<string, any> | undefined,
        categories: Record<string, any>[]
    } {
        if (menuId) {
            const menu = menus.find((menu: any) => menu.id === menuId);
            const menuCategories: Record<string, any>[] = [...(menu?.categories ?? [])];
            return {
                categories: categories.filter((category: Record<string, any>) => menuCategories.map((menuCategory) => menuCategory.category_id).includes(category.id)),
                menu
            }
        } else {
            return {
                menu: undefined,
                categories: []
            };
        }
    }

    static getParsedCategories(
        categories: any[],
        items: any[],
        availabilities: any[],
        availablePromotions: any[],
        session: any,
        cart: any,
        itemGroups: any[],
        menus: any[],
        ageAccepted: boolean,
        isMultilevelMenu?: boolean,
        menuId?: number,
        readonly?: boolean
    ): ParsedCategory[] {
        let finalCategories: Record<string, any>[] = [];
        const {menu, categories: menuCategories} = CategoryHelper.findCategoriesForMenu(categories, menus, menuId);

        if (isMultilevelMenu) {
            finalCategories = [...menuCategories];
        } else {
            finalCategories = [...categories];
        }

        const parsedCategories = finalCategories
            .filter((category: any) => category.status === 'ENABLED')
            .map((category: any) => {
                const menusForCategory = isMultilevelMenu ? (menu ? [JSON.parse(JSON.stringify(menu))] : []) : CategoryHelper.getMenusForCategory(menus, category);
                const menusIdsForCategory = menusForCategory.map((menu: Record<string, any>) => menu.id);
                const menusAvailabilitiesIds = menusForCategory.map((menu: Record<string, any>) => menu.availability_id).filter(Boolean);

                const newCategory: ParsedCategory = {
                    id: category.id,
                    position: this.getCategoryPosition(category, isMultilevelMenu ? menusForCategory : menus),
                    name: category.translation.name,
                    image_link: category.image_link,
                    description: category.translation.description,
                    entities: [],
                    menuIds: [...menusIdsForCategory],
                    originalIds: [category.id],
                };

                newCategory.entities = category.entities.map((entity: any) => {
                    if (entity.type === "ITEM") {
                        const item = items.find((item: any) => item.id === entity.entity_id);
                        if (!item) return null;

                        const parsedItem = ItemViewHelper.parseItem(
                            item, availabilities, availablePromotions,
                            session.pickup_at, entity.position, cart, readonly ? undefined : session.order_type,
                            readonly ? {skipType: true, skipAvailability: true} : undefined
                        );

                        if (parsedItem.alcohol && !ageAccepted) return null;

                        return {
                            ...parsedItem,
                            menuAvailabilitiesIds: [...menusAvailabilitiesIds]
                        }
                    }
                }).filter(Boolean);

                if (itemGroups && itemGroups.length > 0) {
                    newCategory.entities = newCategory.entities
                        .map((entity: any) => {
                            let itemGroupForItem = ItemHelper.getItemGroup(itemGroups, entity.id);
                            if (!itemGroupForItem) return entity;

                            const itemGroupItems = itemGroupForItem.items.filter((itemGroupItem: Record<string, any>) => {
                                return !!newCategory.entities.find((entity: Record<string, any>) => itemGroupItem.item_id === entity.id)
                            }).sort((a: Record<string, any>, b: Record<string, any>) => a.position - b.position);

                            itemGroupForItem = {
                                ...itemGroupForItem,
                                items: [...itemGroupItems]
                            }

                            const parsedItemGroup = ItemViewHelper.parseItemGroup(
                                itemGroupForItem, items, availabilities, availablePromotions,
                                session.pickup_at, entity.position, cart, readonly ? undefined : session.order_type, readonly
                            );

                            if (!ageAccepted) {
                                const nonAlcoholItems = parsedItemGroup.items.filter((item: Record<string, any>) => !item.alcohol);

                                if (nonAlcoholItems.length === 0) return null;

                                return {
                                    ...parsedItemGroup,
                                    items: [...nonAlcoholItems],
                                    item: nonAlcoholItems[0],
                                    menuAvailabilitiesIds: [...menusAvailabilitiesIds]
                                }
                            }

                            return {
                                ...parsedItemGroup,
                                menuAvailabilitiesIds: [...menusAvailabilitiesIds]
                            }
                        }).filter(Boolean)
                        .reduce((acc: any[], entity: any) => {
                            if (!entity.reference_id) {
                                acc.push(entity);
                                return acc;
                            }

                            const existing = acc.find(x => x.reference_id === entity.reference_id);
                            if (existing) {
                                if (entity.position < existing.position) existing.position = entity.position;
                            } else {
                                acc.push(entity);
                            }
                            return acc;
                        }, []);
                }

                return newCategory.entities.length > 0 ? newCategory : null;
            })
            .filter((category): category is ParsedCategory => category !== null);

        return Object.values(
            parsedCategories.reduce((acc: any, category) => {
                const key = category.name;

                if (!acc[key]) {
                    acc[key] = {
                        ...category,
                        entities: [...category.entities],
                        menuIds: [...category.menuIds],
                        originalIds: [...category.originalIds]
                    };
                } else {
                    category.entities.forEach(entity => {
                        const existing = acc[key].entities.find((accEntity: any) => accEntity.id === entity.id);

                        if (!existing) {
                            acc[key].entities.push(entity);
                        } else {
                            if (entity.position < existing.position) {
                                existing.position = entity.position;
                            }

                            existing.menuAvailabilitiesIds = Array.from(
                                new Set([...(existing.menuAvailabilitiesIds || []), ...(entity.menuAvailabilitiesIds || [])])
                            );
                        }
                    });

                    acc[key].originalIds.push(category.id);
                    acc[key].menuIds = Array.from(
                        new Set([...acc[key].menuIds, ...category.menuIds])
                    );
                }

                return acc;
            }, {})
        );
    }

    static getSortedParsedCategoriesByMenu(categories: ParsedCategory[], menus: Record<string, any>[]): ParsedCategory[] {
        const menuMap = new Map<number, any>();

        menus.forEach(menu => {
            if (menu && menu.id) menuMap.set(menu.id, menu);
        });

        return [...categories].sort((a, b) => {
            const aMenuNames = (a.menuIds || [])
                .map((id) => menuMap.get(id)?.name)
                .filter(Boolean)
                .sort();

            const bMenuNames = (b.menuIds || [])
                .map((id) => menuMap.get(id)?.name)
                .filter(Boolean)
                .sort();

            const aMain = aMenuNames[0] || "";
            const bMain = bMenuNames[0] || "";

            const nameComparison = aMain.localeCompare(bMain);
            if (nameComparison !== 0) return nameComparison;

            return a.position - b.position;
        });
    }

    static getSortedCategoryItems(category: any): any[] {
        return [...category.entities].sort((a: any, b: any) => a.position - b.position || a.name.localeCompare(b.name));
    }

    static getAvailabilityInfo(category: any, menusWithCategory: any[], items: any[], availabilities: any[],
                               config: ConfigApi | undefined, orderType: OrderType | undefined, pickupDate: string | undefined,
                               t: TFunction, locale?: string, startFormattedDateWithUpperCase?: boolean): {
        text: string;
        typesText?: string;
        dateText?: string;
        currentType?: boolean;
        isNowAvailable?: boolean;
        includeCurrentType?: boolean
    }[] {
        const menuAvailabilitiesForOrderTypes = Object.keys(OrderType).reduce((acc: any, type: string) => {
            const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(type).toLowerCase()] === true);
            const menuAvailabilitiesForType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForOrderType.map((menu: any) => menu.availability_id));
            const maxSearchDate = DateService.emptyOrNow(pickupDate);
            maxSearchDate.setDate(maxSearchDate.getDate() + 30);
            const date = AvailableHelper.getNearestOpenDateFromNowOrDate(menuAvailabilitiesForType, DateService.emptyOrNow(pickupDate), true, maxSearchDate);
            acc[type] = {
                availabilities: menuAvailabilitiesForType,
                date,
            }
            return acc;
        }, {});

        const storeOpenHours = config?.hours || [];

        const orderTypesAvailabilities = (Object.keys(OrderType) as OrderType[]).reduce((acc: any, type) => {
            const menuAvailabilitiesForType = menuAvailabilitiesForOrderTypes[type];
            const menuAvailabilitiesIds = menuAvailabilitiesForType.availabilities.map((availability: Record<string, any>) => availability.id);
            const itemsForMenuAvailability = items.filter((item: Record<string, any>) => item.menuAvailabilitiesIds.some((id: number) => menuAvailabilitiesIds.includes(id)));

            if (itemsForMenuAvailability.length > 0 && itemsForMenuAvailability.every((item: Record<string, any>) => ItemAvailabilityHelper.checkIfItemIsDisabledForOrderType(item, type))) {
                acc[type] = undefined;
                return acc;
            }

            const anyItemAlwaysAvailable = itemsForMenuAvailability.some((item: any) => item.type === "ITEM_GROUP" ?
                item.items.some((itemItem: Record<string, any>) => itemItem.item_always_available && !ItemAvailabilityHelper.checkIfItemIsDisabledForOrderType(itemItem, type)) :
                item.item_always_available && !ItemAvailabilityHelper.checkIfItemIsDisabledForOrderType(item, type));
            const itemsWithRestrictions = ItemAvailabilityHelper.getFlattedNotAlwaysAvailableItems(itemsForMenuAvailability).filter(item => item.item_nearest_available_date_per_type)
                .sort((a: any, b: any) => a.item_nearest_available_date_per_type[type]?.dateFrom?.getTime() - b.item_nearest_available_date_per_type[type]?.dateFrom?.getTime());

            let nearestAvailableDate = undefined;
            let searchDate = DateService.emptyOrNow(pickupDate);

            const maxSearchTries = 30;
            let currentSearchTry = 0;
            while (!nearestAvailableDate && itemsWithRestrictions.length && !anyItemAlwaysAvailable) {
                if (currentSearchTry >= maxSearchTries) break;

                for (const itemWithRestriction of itemsWithRestrictions) {
                    const maxSearchDate = new Date(searchDate);
                    maxSearchDate.setDate(maxSearchDate.getDate() + 30);
                    const nextItemAvailableDate = ItemHelper.getNearestAvailabilityDateForItem(itemWithRestriction, availabilities, searchDate ? DateHelper.format(searchDate) : undefined, type, maxSearchDate);

                    if (nextItemAvailableDate.dateFrom) {
                        AvailableHelper.trimHoursToAvailabilities(menuAvailabilitiesForType.availabilities, nextItemAvailableDate);
                        HoursHelper.trimHoursToStore(storeOpenHours, nextItemAvailableDate);
                    }

                    if (nextItemAvailableDate.dateTo?.getTime() === nextItemAvailableDate.dateFrom?.getTime()) {
                        searchDate.setDate(searchDate.getDate() + 1);
                        searchDate.setHours(0, 0, 0, 0);
                        currentSearchTry++;
                        continue;
                    }

                    if (nextItemAvailableDate.dateFrom
                        && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, nextItemAvailableDate.dateFrom)
                        && searchDate.getTime() <= nextItemAvailableDate.dateFrom.getTime()
                    ) {
                        nearestAvailableDate = nextItemAvailableDate;
                        break;
                    } else if (nextItemAvailableDate.dateTo && nextItemAvailableDate.dateFrom) {
                        const compareDateTo = new Date(nextItemAvailableDate.dateTo);
                        if (compareDateTo <= nextItemAvailableDate.dateFrom) {
                            compareDateTo.setDate(compareDateTo.getDate() + 1);
                        }
                        if (AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, nextItemAvailableDate.dateFrom)
                            && searchDate.getTime() < compareDateTo.getTime()) {
                            nearestAvailableDate = {
                                ...nextItemAvailableDate,
                                dateTo: compareDateTo
                            };
                            break;
                        }
                    }
                }

                searchDate.setDate(searchDate.getDate() + 1);
                searchDate.setHours(0, 0, 0, 0);
                currentSearchTry++;
            }

            if (!nearestAvailableDate) {
                currentSearchTry = 0;
                searchDate = DateService.emptyOrNow(pickupDate);
                while (!nearestAvailableDate && (itemsWithRestrictions.length === 0 || anyItemAlwaysAvailable)) {
                    if (currentSearchTry >= maxSearchTries) break;
                    for (const availability of menuAvailabilitiesForType.availabilities) {
                        const availabilityDate = new Date(searchDate);
                        const date: { dateFrom: Date; dateTo?: Date } = {
                            dateFrom: new Date(availabilityDate),
                            dateTo: undefined,
                        };
                        AvailableHelper.trimHoursToAvailabilities([availability], date);
                        HoursHelper.trimHoursToStore(storeOpenHours, date);

                        if (date.dateTo?.getTime() === date.dateFrom?.getTime()) {
                            searchDate.setDate(searchDate.getDate() + 1);
                            searchDate.setHours(0, 0, 0, 0);
                            currentSearchTry++;
                            continue;
                        }

                        if (date.dateFrom && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, date.dateFrom)
                            && searchDate.getTime() <= date.dateFrom?.getTime()) {
                            nearestAvailableDate = date;
                            break;
                        } else if (date.dateTo) {
                            const compareDateTo = new Date(date.dateTo);
                            if (compareDateTo <= date.dateFrom) {
                                compareDateTo.setDate(compareDateTo.getDate() + 1);
                            }
                            if (AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, date.dateFrom)
                                && searchDate.getTime() < compareDateTo.getTime()) {
                                nearestAvailableDate = {
                                    ...date,
                                    dateTo: compareDateTo
                                };
                                break;
                            }
                        }
                    }

                    searchDate.setDate(searchDate.getDate() + 1);
                    searchDate.setHours(0, 0, 0, 0);
                    currentSearchTry++;
                }
            }

            acc[type] = nearestAvailableDate;

            return acc;
        }, {});

        const allTypesAvailabilities = Object.keys(orderTypesAvailabilities)
            .map(type => {
                if (!orderTypesAvailabilities[type]) return undefined;

                return orderTypesAvailabilities[type];
            })
            .filter(Boolean)
            .sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime());

        let infos: {
            text: string;
            typesText?: string;
            dateText?: string;
            currentType?: boolean;
            isNowAvailable?: boolean;
            includeCurrentType?: boolean;
        }[] = [];

        const otherTypesDateGroups: {
            types: string[];
            dateFrom: Date;
            dateTo: Date;
            isNowAvailable: boolean;
        }[] = [];

        if (allTypesAvailabilities.length) {
            Object.keys(orderTypesAvailabilities)
                .forEach(type => {
                    const orderTypesAvailability = orderTypesAvailabilities[type];
                    if (orderTypesAvailability) {
                        const isNowAvailable = orderTypesAvailability.dateFrom && orderTypesAvailability.dateTo && new Date() >= orderTypesAvailability.dateFrom && new Date() < orderTypesAvailability.dateTo;
                        let index = otherTypesDateGroups.findIndex(group => (group?.dateFrom.getTime() === orderTypesAvailability?.dateFrom.getTime() && group.dateTo?.getTime() === orderTypesAvailability?.dateTo.getTime()));

                        if (isNowAvailable) {
                            index = otherTypesDateGroups.findIndex(group => group.isNowAvailable);
                        }

                        if (index > -1) {
                            otherTypesDateGroups[index].types.push(type);
                        } else {
                            otherTypesDateGroups.push({
                                types: [type],
                                dateFrom: orderTypesAvailability.dateFrom,
                                dateTo: orderTypesAvailability.dateTo,
                                isNowAvailable,
                            })
                        }
                    }
                });

            otherTypesDateGroups.forEach(group => {
                infos.push({
                    text: `${t("modules.menu.field.available.title")}:`,
                    typesText: group.types.map(type => t(`enums.order.type.${type}`)).join(", "),
                    dateText: `${DateHelper.getFormattedNearestDate(DateHelper.format(group.dateFrom), t, locale ?? "", startFormattedDateWithUpperCase)} - ${getHourTimeString(group.dateTo)}`,
                    isNowAvailable: group.isNowAvailable,
                    currentType: orderType ? group.types.length === 1 && group.types[0] === orderType : false,
                    includeCurrentType: orderType ? group.types.includes(orderType) : false,
                })
            });
        }

        return infos.length > 0 ? infos : [{text: t("common.word.not_available")}];
    };

    static getSortedCategoriesByPosition(categories: ParsedCategory[]) {
        return [...categories].sort((a, b) => a.position - b.position);
    }
}

export default CategoryHelper;